import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface LoadingScreenProps {
  onLoadingComplete: () => void;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const { t } = useTranslation("common");

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const simulateProgress = () => {
      if (progress >= 100) {
        timeoutId = setTimeout(() => {
          onLoadingComplete();
        }, 800);
        return;
      }

      const increment = progress < 80 ? 4 : 2;
      const delay = progress < 80 ? 20 : 40;

      timeoutId = setTimeout(() => {
        setProgress((prev) => Math.min(prev + increment, 100));
      }, delay);
    };

    simulateProgress();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [progress, onLoadingComplete]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #2F0D68 0%, #1A0B2E 100%, #240D49 100%)",
      }}
    >
      {/* Background Decorative Glows */}
      <div className="absolute top-1/4 -left-20 w-[400px] h-[400px] bg-[#5a18c4] opacity-20 rounded-full blur-[100px] pointer-events-none px-6" />
      <div className="absolute bottom-1/4 -right-20 w-[400px] h-[400px] bg-[#ff7c67] opacity-10 rounded-full blur-[100px] pointer-events-none px-6" />

      <div className="relative z-10 flex flex-col items-center px-6 text-center max-w-md w-full">
        {/* Logo Container */}
        <div className="mb-12 relative">
          <div className="absolute inset-0 bg-white opacity-20 blur-2xl rounded-full scale-150 animate-pulse" />
          <img
            src="/gear-surf.svg"
            alt="Logo"
            className="h-20 w-auto brightness-0 invert relative z-10"
          />
        </div>

        <h1 className="text-3xl font-[600] text-white mb-2 tracking-tight">
          {t("loading.welcome", "Benvenuto!")}
        </h1>

        {/* Progress Bar Container */}
        <div className="w-full mt-10">
          <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden backdrop-blur-sm border border-white/10 p-[1px]">
            <div
              className="h-full bg-secondary rounded-full transition-all duration-300 ease-out shadow-[0_0_15px_rgba(255,124,103,0.6)]"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, #ff7c67 0%, #ff9b82 100%)",
              }}
            />
          </div>

          <div className="mt-8 flex flex-col items-center gap-3">
            <p className="text-white/60 text-sm font-medium tracking-wide min-h-[1.5rem] transition-all duration-500">
              {progress < 30 &&
                t("loading.initializingMap", "Inizializzazione mappa...")}
              {progress >= 30 &&
                progress < 60 &&
                t("loading.loadingData", "Caricamento dati...")}
              {progress >= 60 &&
                progress < 85 &&
                t("loading.preparingMarkers", "Preparazione markers...")}
              {progress >= 85 && t("loading.almostReady", "Quasi pronto...")}
            </p>
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 bg-secondary rounded-full animate-bounce [animation-delay:-0.3s]" />
              <div className="w-1 h-1 bg-secondary rounded-full animate-bounce [animation-delay:-0.15s]" />
              <div className="w-1 h-1 bg-secondary rounded-full animate-bounce" />
              <span className="text-white/40 text-xs font-mono ml-2">
                {progress}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer copyright style text */}
      <div className="absolute bottom-10 text-white/10 text-[10px] tracking-[0.3em] uppercase font-bold">
        GEAR SURFING
      </div>

      {/* Custom Styles for extra polish */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.15; transform: scale(1.5); }
          50% { opacity: 0.25; transform: scale(1.7); }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
